"use client";

import { ArrowsClockwiseIcon } from "@phosphor-icons/react";
import Image from "next/image";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Typography } from "@/components/ui/typography";
import { useVersionCheckContext, VersionPollingProvider } from "@/contexts/version-check-context";
import { useTranslation } from "@/i18n/client";
import { installChunkErrorRecovery } from "@/utils/version";
import { MaintenanceLock } from "./maintenance-lock";
import styles from "./version-check-provider.module.css";

interface UpdateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  version?: string;
  releaseNotes?: string[];
  onUpdate: () => void;
}

function UpdateDialog({ isOpen, onClose, releaseNotes, onUpdate }: UpdateDialogProps) {
  const { t: tVersion } = useTranslation("version-check-provider");
  const [isUpdating, setIsUpdating] = useState(false);

  const handleUpdate = async () => {
    setIsUpdating(true);
    await new Promise((resolve) => window.setTimeout(resolve, 1000));
    onUpdate();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="p-0 h-[400px] max-sm:w-[300px] max-sm:max-w-[300px] sm:w-[400px] sm:max-w-[400px] border-0 box-content"
        hasCloseButton={true}
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
      >
        <DialogTitle className="sr-only">{tVersion("title")}</DialogTitle>
        <div className="relative flex flex-col w-full">
          <div
            className={`relative ${styles.gradientShiftAnimation} px-6 py-16 rounded-lg text-white overflow-hidden`}
          >
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <div className="relative w-16 h-16">
                <div
                  className={`absolute w-2.5 h-2.5 bg-white/40 rounded-full ${styles.orbitalAnimation} ${styles.delay0s}`}
                />
                <div
                  className={`absolute w-2 h-2 bg-white/50 rounded-full ${styles.orbitalAnimation} ${styles.delay2s} ${styles.duration6s}`}
                />
                <div
                  className={`absolute w-1.5 h-1.5 bg-white/45 rounded-full ${styles.orbitalAnimation} ${styles.delay4s} ${styles.duration10s}`}
                />
              </div>
            </div>

            <div className="absolute inset-0 pointer-events-none">
              <div
                className={`absolute top-4 left-4 w-1.5 h-1.5 bg-white/30 rounded-full ${styles.floatAnimation} ${styles.delay0s}`}
              />
              <div
                className={`absolute top-8 right-6 w-1 h-1 bg-white/35 rounded-full ${styles.floatAnimation} ${styles.delay1s}`}
              />
              <div
                className={`absolute bottom-6 left-8 w-1 h-1 bg-white/30 rounded-full ${styles.floatAnimation} ${styles.delay2s}`}
              />
              <div
                className={`absolute bottom-8 right-4 w-2 h-2 bg-white/25 rounded-full ${styles.floatAnimation} ${styles.delay1_5s}`}
              />
            </div>

            <div className="relative z-10 flex justify-center mb-6">
              <div
                className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/15 backdrop-blur-sm ${styles.morphingGlowAnimation}`}
              >
                <Image
                  src="/images/logos/evaluar-isotype.svg"
                  alt="Evaluar Isotype"
                  width={32}
                  height={32}
                />
              </div>
            </div>

            <div className="absolute bottom-0 left-0 w-full h-4">
              <div
                className={`absolute bottom-0 left-0 w-full h-3 bg-gradient-to-r from-transparent via-white/8 to-transparent transform rotate-1 ${styles.wavePulseAnimation}`}
              />
              <div
                className={`absolute bottom-0.5 left-0 w-full h-1.5 bg-gradient-to-r from-transparent via-white/5 to-transparent transform -rotate-1 ${styles.wavePulseAnimation} ${styles.delay1s}`}
              />
            </div>
          </div>

          <div className="p-6 flex-1 flex flex-col">
            <div className="flex-1 space-y-4">
              <div className="space-y-2 flex flex-col">
                <Typography variant="heading 4" className="text-neutral-700 font-semibold">
                  {tVersion("title")}
                </Typography>
                <Typography variant="body 2" className="text-neutral-600">
                  {tVersion("desc")}
                </Typography>
              </div>

              {releaseNotes && releaseNotes.length > 0 && (
                <div className="space-y-2">
                  <Typography variant="label 3">{tVersion("newsTitle")}</Typography>
                  <ul className="space-y-1">
                    {releaseNotes.map((note, index) => (
                      <li
                        key={index}
                        className="text-sm text-gray-600 dark:text-gray-300 flex items-start gap-2"
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 ${styles.noteBullet}`}
                        />
                        {note}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-4 mt-auto">
              <Button variant="outline" onClick={onClose} disabled={isUpdating} className="flex-1">
                {tVersion("later")}
              </Button>
              <Button
                onClick={handleUpdate}
                disabled={isUpdating}
                variant="primary"
                className="flex-1"
              >
                {isUpdating ? (
                  <>
                    <ArrowsClockwiseIcon className="w-4 h-4 mr-2 animate-spin" />
                    {tVersion("updating")}
                  </>
                ) : (
                  tVersion("refresh")
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function VersionDialog() {
  const { updateAvailable, maintenanceActive, latestVersion, dismiss, reloadNow } =
    useVersionCheckContext();
  if (maintenanceActive) {
    return <MaintenanceLock />;
  }
  return (
    <UpdateDialog
      isOpen={updateAvailable}
      onClose={dismiss}
      version={latestVersion ?? undefined}
      releaseNotes={undefined}
      onUpdate={reloadNow}
    />
  );
}

export default function VersionCheckProvider() {
  useEffect(() => {
    installChunkErrorRecovery();
  }, []);
  return (
    <VersionPollingProvider intervalMs={60000}>
      <VersionDialog />
    </VersionPollingProvider>
  );
}
